import type { Notification, NotificationsResponse } from '../types/notifications.types'
import { API_ENDPOINTS } from '../../../constants/api'
import type { PushPlatform } from '../types/notifications.types'

/** Minimal shape of the authenticated client returned by useApiClient(). */
export type ApiClient = (path: string, options?: Record<string, unknown>) => Promise<{ data: unknown }>

/** Register/refresh this device's Expo push token (and its timezone for quiet hours). */
export async function registerPushToken(
  apiClient: ApiClient,
  params: { token: string; platform: PushPlatform; timezone: string },
): Promise<void> {
  const { data } = await apiClient(API_ENDPOINTS.users.pushToken, {
    method: 'POST',
    data: params,
  })
  const res = data as { success: boolean }
  if (!res.success) throw new Error('Failed to register push token')
}

export async function fetchNotifications(
  apiClient: ApiClient,
  limit = 20,
): Promise<{ notifications: Notification[]; unreadCount: number }> {
  const { data } = await apiClient(`/notifications?limit=${limit}`)
  const res = data as NotificationsResponse
  if (!res.success) throw new Error('Failed to fetch notifications')
  return { notifications: res.data, unreadCount: res.unreadCount }
}

export async function markNotificationRead(
  apiClient: ApiClient,
  notificationId: string,
): Promise<Notification> {
  const { data } = await apiClient(`/notifications/${notificationId}/read`, {
    method: 'PATCH',
  })
  const res = data as { success: boolean; data: Notification }
  if (!res.success) throw new Error('Failed to mark notification as read')
  return res.data
}

export async function markAllNotificationsRead(
  apiClient: ApiClient,
): Promise<number> {
  const { data } = await apiClient('/notifications/read-all', {
    method: 'PATCH',
  })
  const res = data as { success: boolean; markedRead: number }
  if (!res.success) throw new Error('Failed to mark all notifications as read')
  return res.markedRead
}
